// react
// dependencies
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GenerateProcess } from "redux-process";

// components
import Modal from "../../components/Modal";
import Confirm from "./components/Confirm";
import Results from "./components/Results";
import SelectAuthor from "./components/SelectAuthor";
import SelectEasyAddForm from "./components/SelectEasyAddForm";
import SelectEasyAddReview from "./components/SelectEasyAddReview";

// selectors
import { getUserName } from "../../selectors/app";

import PropTypes from "../../lib/prop-types";

import Button from "../Button";

const AddFormModal = (props) => {
	const { handleClose, userID, employeeID, reviewData = { id: null, name: null }, reviews } = props;
	const [contentType, setContentType] = useState("review");
	const [easyAddForms, setEasyAddForms] = useState([]);
	const [addFormData, setAddFormData] = useState({
		review: reviewData ? { id: reviewData.id, name: reviewData.name } : {},
		form: {},
		authors: [],
		formComment: "",
		characterCount: 0,
	});
	const [submitting, setSubmitting] = useState(false);
	const [results, setResults] = useState({
		forms_added: [],
		forms_not_added: [],
		forms_require_approval: [],
	});

	const { authors, formComment, characterCount } = addFormData;

	const dispatch = useDispatch();

	const userName = useSelector((state) => getUserName(state));

	const successfullyAddedForm = results.forms_added.length > 0 || results.forms_require_approval.length > 0;

	const handleEdit = (type) => {
		setContentType(type);
	};

	const handleSetEasyAddForms = (forms) => {
		setEasyAddForms(forms);
	};

	useEffect(() => {
		// for view-review page, pre-populate the review value
		if (reviewData.id !== null) {
			handleSetEasyAddForms(reviews.reviews[0].easy_add_forms);
		}
	}, []);

	const handleSetAddForm = (val, type) => {
		setAddFormData({ ...addFormData, [type]: val });
	};

	const handleCancel = () => {
		setAddFormData({ review: {}, form: {}, authors: [] });
		handleClose(successfullyAddedForm);
	};

	const contentTypes = {
		review: (
			<SelectEasyAddReview
				userID={userID}
				employeeID={employeeID}
				reviewData={reviewData}
				handleSetAddForm={handleSetAddForm}
				addFormData={addFormData}
				handleSetEasyAddForms={handleSetEasyAddForms}
				userReviews={reviews}
			/>
		),
		form: (
			<SelectEasyAddForm
				handleSetAddForm={handleSetAddForm}
				addFormData={addFormData}
				easyAddForms={easyAddForms}
			/>
		),
		authors: (
			<SelectAuthor
				handleSetAddForm={handleSetAddForm}
				authors={authors}
				formComment={formComment}
				form={addFormData.form}
			/>
		),
		confirm: <Confirm addFormData={addFormData} handleEdit={handleEdit} />,
		results: <Results results={results} addFormData={addFormData} userID={userID} />,
	};

	const modalNames = ["review", "form", "authors", "confirm", "results"];

	const handleNext = () => {
		let nextIndex = modalNames.indexOf(contentType) + 1;
		if (contentType === "form") {
			const anyEmployee = addFormData.form.whoCanAddForm.any_employee;
			const subjectSelf = addFormData.form.subject === "self";
			if (anyEmployee || subjectSelf) {
				setAddFormData({
					...addFormData,
					authors: [{ name: userName, id: userID, noEdit: true }],
				});
				if (!addFormData.form.allowComments) {
					// no option to add authors or comments, skip to confirm
					nextIndex += 1;
				}
			} else {
				setAddFormData({ ...addFormData, authors: [] });
			}
		}
		setContentType(modalNames[nextIndex]);
	};

	const handleBack = () => {
		const nextIndex = modalNames.indexOf(contentType) - 1;
		setContentType(modalNames[nextIndex]);
	};

	const handleAddForm = async () => {
		setSubmitting(true);
		const authorIDs = addFormData.authors.map((author) => author.id);
		const formData = {
			authors: authorIDs,
			formID: addFormData.form.id,
			formComment,
		};
		const response = await dispatch(
			GenerateProcess("addFormToReview", {
				payload: formData,
				reviewID: addFormData.review.id,
				easyAddID: addFormData.form.easyAddConfigID,
			})
		);

		if (response.status === 201) {
			setSubmitting(false);
			setResults(response.response);
			handleNext();
		}
		setSubmitting(false);
	};

	const buttons = (
		<div style={{ display: "flex", gap: "8px", justifyContent: "space-evenly" }}>
			{contentType !== "review" && contentType !== "confirm" && contentType !== "results" ? (
				<Button color="gray" isHollow={true} onClick={handleBack}>
					Back
				</Button>
			) : null}
			{contentType !== "confirm" && contentType !== "results" ? (
				<Button
					color="blue"
					onClick={handleNext}
					disabled={
						(!addFormData[contentType].id && contentType !== "authors") ||
						(contentType === "authors" && !authors.length) ||
						characterCount > 500
					}
				>
					Next
				</Button>
			) : null}
			{contentType === "confirm" ? (
				<Button color="green" onClick={() => handleAddForm(addFormData)} isRequesting={submitting}>
					Confirm
				</Button>
			) : null}
			{contentType === "results" ? (
				<Button color="blue" onClick={handleCancel}>
					Close
				</Button>
			) : null}
		</div>
	);
	return <Modal size="large" handleClose={handleCancel} buttons={buttons} content={contentTypes[contentType]} />;
};

AddFormModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	userID: PropTypes.string.isRequired,
	employeeID: PropTypes.string.isRequired,
	reviewData: PropTypes.object,
	reviews: PropTypes.object.isRequired,
};

export default AddFormModal;
